<template>
  <div class="flex flex-col min-h-full">
    <div class="flex-none bg-blue-900 p-4 lg:p-12">
      <div class="w-full mx-auto mb-12 space-y-4">
        <div class="flex flex-col md:flex-row md:items-center">
          <h4 class="text-white font-bold">Tasks</h4>

          <div class="flex md:ml-auto mt-4 md:mt-0">
            <div class="glue-h">
              <BaseButton primary :outline="!isTableView" @click="setTableView">
                Table List
              </BaseButton>
              <BaseButton primary :outline="!isKanbanView" @click="setKanbanView">
                Kanban board
              </BaseButton>
            </div>
            <BaseButton info class="ml-auto md:ml-4" @click="filtersVisible = !filtersVisible">
              Filters
            </BaseButton>
          </div>
        </div>
        <div class="glue-h">
          <BaseButton primary small :outline="isScopedToMe" @click="showAssignedToMe(false)">
            All
          </BaseButton>
          <BaseButton primary small :outline="!isScopedToMe" @click="showAssignedToMe">
            Assigned to Me
          </BaseButton>
        </div>
      </div>
    </div>
    <div
      class="flex-1 lg:px-12 -mt-12 h-0 flex"
      :class="[
        {
          'lg:pb-12': isTableView,
          'px-4': isTableView,
          'pb-4': isTableView,
        },
      ]"
    >
      <div class="w-full mx-auto">
        <div
          class="space-y-16"
          :class="[
            {
              'h-full': isKanbanView,
            },
          ]"
        >
          <Filters
            v-if="filtersVisible"
            :filters="filters"
            @filters:applied="filtersVisible = false"
            @filters:cancel="filtersVisible = false"
          />

          <VKanbanBoard v-if="isKanbanView" :lists="statuses">
            <template #default="taskListProps">
              <KanbanBoardTaskList
                :key="taskListProps.list"
                :ref="taskListProps.list"
                :task-status="taskListProps.list"
                :filters="queryFilters"
                :last-edited-task-id="lastEditedTaskId"
                :fetch-tasks="fetchTasks"
                @selected-task="setLastEditedTask"
                @task-list:updated="refreshLists"
              />
            </template>
          </VKanbanBoard>

          <TaskList
            v-else
            :account-id="accountId"
            :last-edited-task-id="lastEditedTaskId"
            :fetch-tasks="fetchTasks"
            @selected-task="setLastEditedTask"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { omit } from 'lodash-es';
import { accountTasksClient } from '@/api/clients';
import VKanbanBoard from '@/components/Common/VKanbanBoard.vue';
import TaskList from '@/components/Tasks/AccountTaskList.vue';
import KanbanBoardTaskList from '@/components/Tasks/KanbanBoardTaskList.vue';
import Filters from '@/components/Tasks/Filters.vue';

const VIEW_TABLE = 'table';
const VIEW_KANBAN = 'kanban';

const STATUS_PENDING = 'pending';
const STATUS_PENDING_CORRECTIONS = 'pending corrections';
const STATUS_IN_PROGRESS = 'in progress';
const STATUS_IN_REVIEW = 'in review';
const STATUS_COMPLETED = 'completed';

export default {
  name: 'Tasks',

  components: {
    TaskList,
    VKanbanBoard,
    KanbanBoardTaskList,
    Filters,
  },

  props: {
    accountId: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    view: VIEW_KANBAN,
    lastEditedTaskId: null,
    selectedTaskId: null,
    filters: {},
    queryFilters: {},
    statuses: [
      STATUS_PENDING,
      STATUS_PENDING_CORRECTIONS,
      STATUS_IN_PROGRESS,
      STATUS_IN_REVIEW,
      STATUS_COMPLETED,
    ],
    filtersVisible: false,
  }),

  computed: {
    isKanbanView() {
      return this.view === VIEW_KANBAN;
    },
    isTableView() {
      return this.view === VIEW_TABLE;
    },
    isScopedToMe() {
      return 'assigned_to' in this.queryFilters;
    },
  },

  watch: {
    $route: 'updateQueryFilters',
  },

  created() {
    this.fetchFilters();
  },

  methods: {
    showAssignedToMe(assignedToMe = true) {
      const { filter = {} } = this.$route.query;

      if (assignedToMe) {
        this.queryFilters = { ...omit(filter, ['assigned_to']), assigned_to: this.$user.id };
      } else {
        this.queryFilters = { ...omit(filter, ['assigned_to']) };
      }
    },
    setTableView() {
      this.view = VIEW_TABLE;
    },
    setKanbanView() {
      this.view = VIEW_KANBAN;
    },
    async fetchFilters() {
      try {
        const response = await accountTasksClient.filters(this.accountId);
        this.filters = response.data.data;
      } catch (error) {
        /** handle error */
      }
    },

    async fetchTasks(params) {
      return accountTasksClient.list(this.accountId, params);
    },

    updateQueryFilters({ query }) {
      const { filter = {} } = query;

      this.queryFilters = { ...filter };
    },

    setLastEditedTask(taskId) {
      this.lastEditedTaskId = taskId;
    },

    refreshLists(lists) {
      lists.forEach((list) => {
        const refs = this.$refs[list];

        if (!refs) {
          return;
        }

        if (Array.isArray(refs)) {
          refs.forEach((ref) => {
            ref.refresh();
          });
          return;
        }

        refs.refresh();
      });
    },
  },
};
</script>
